import React, { useState } from "react";
import { subYears } from "date-fns";
import styles from "./UserInfoForm.module.css";
import { useWebTranslation, Trans } from "translations";
import ChatMessage from "ui-kit/ChatMessage/ChatMessage";
import { StackedTextInput } from "ui-kit/TextInput/TextInput";
import { validateEmail } from "utils/validation";
import { StackedDateInput } from "components/DateInput/DateInput";
import Gutter from "ui-kit/Gutter";
import { Typography } from "ui-kit/Typography";
import { FlexRow } from "ui-kit/Layout/Flex";
import { Checkbox } from "ui-kit/Checkbox";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { updateCustomer } from "components/Signup/store/slices/customer";
import { useCustomer } from "components/Signup/store/selectors/customer";
import { useHasLiabilityInsurance } from "components/Signup/store/selectors/animals";

const UserInfoForm = () => {
  const { t } = useWebTranslation();
  const [emailValid, setEmailValid] = useState(true);
  const [dateValid, setDateValid] = useState(true);
  const [dateValidMin, setDateValidMin] = useState(true);
  const [dateValidMax, setDateValidMax] = useState(true);

  const dispatch = useSignupDispatch();
  const customer = useCustomer();
  const hasLiabilityInsurance = useHasLiabilityInsurance();

  const minAge = subYears(new Date(), 120);
  const maxAge = subYears(new Date(), 18);

  return (
    <>
      <ChatMessage fromMe unSent small border={false}>
        <StackedTextInput
          first
          placeHolder={t("global.first_name")}
          value={customer.firstName ?? ""}
          onChangeText={(firstName) => {
            dispatch(updateCustomer({ firstName }));
          }}
          autoComplete="given-name"
        />
        <StackedTextInput
          placeHolder={t("global.last_name")}
          value={customer.lastName ?? ""}
          onChangeText={(lastName) => {
            dispatch(updateCustomer({ lastName }));
          }}
          autoComplete="family-name"
        />
        <StackedTextInput
          placeHolder={t("global.email")}
          value={customer.email ?? ""}
          onChangeText={(email) => {
            if (!emailValid) {
              setEmailValid(validateEmail(email));
            }
            dispatch(updateCustomer({ email }));
          }}
          onBlur={(e) => {
            setEmailValid(validateEmail(e.target.value));
          }}
          name="email"
          autoComplete="email"
          type="email"
        />
        {!hasLiabilityInsurance && (
          <StackedDateInput
            value={customer.birthdate ?? ""}
            onChange={({ date, validation }) => {
              if (!dateValid) {
                setDateValid(validation.isValid);
              }
              setDateValidMin(validation.isValidMin);
              setDateValidMax(validation.isValidMax);
              dispatch(updateCustomer({ birthdate: date }));
            }}
            onBlur={({ validation }) => {
              setDateValid(validation.isValid);
            }}
            min={minAge}
            max={maxAge}
            noValidate
            autoComplete="bday"
          />
        )}
        <StackedTextInput
          placeHolder={t("signup.user_info.street_name_and_number")}
          value={customer.address?.line1 ?? ""}
          onChangeText={(line1) => {
            dispatch(
              updateCustomer({
                address: {
                  line1,
                  city: customer.address?.city ?? null,
                  zip: customer.address?.zip ?? null,
                },
              })
            );
          }}
          autoComplete="street-address"
        />
        <StackedTextInput
          placeHolder={t("signup.user_info.zip")}
          value={customer.address?.zip ?? ""}
          onChangeText={(zip) => {
            dispatch(
              updateCustomer({
                address: {
                  line1: customer.address?.line1 ?? null,
                  city: customer.address?.city ?? null,
                  zip,
                },
              })
            );
          }}
          autoComplete="address-zip"
        />
        <StackedTextInput
          last
          placeHolder={t("signup.user_info.city")}
          value={customer.address?.city ?? ""}
          onChangeText={(city) => {
            dispatch(
              updateCustomer({
                address: {
                  line1: customer.address?.line1 ?? null,
                  city,
                  zip: customer.address?.zip ?? null,
                },
              })
            );
          }}
          autoComplete="address-level2"
        />
        <Gutter size={1} />
        {!emailValid && (
          <>
            <Gutter size={1} />
            <Typography color={"error"} testId="date-error">
              {t("validation.email")}
            </Typography>
            <Gutter size={1} />
          </>
        )}
        {!(dateValid && dateValidMin) && (
          <>
            <Gutter size={1} />
            <Typography color={"error"} testId="date-error">
              {t("date_input.error")}
            </Typography>
            <Gutter size={1} />
          </>
        )}
        {!dateValidMax && (
          <>
            <Gutter size={1} />
            <Typography color={"error"}>
              {t("validation.underage_person")}
            </Typography>
            <Gutter size={1} />
          </>
        )}
        <Gutter size={2} />
        <AcceptBox
          textKey="signup.user_info.privacy_notice.description"
          linkKey="signup.user_info.privacy_notice.link.url"
          selected={customer.privacyAccepted}
          onSelect={() => {
            dispatch(
              updateCustomer({ privacyAccepted: !customer.privacyAccepted })
            );
          }}
        />
        <Gutter size={2} />
        <AcceptBox
          textKey={
            hasLiabilityInsurance
              ? "signup.user_info.terms_liability.description"
              : "signup.user_info.terms.description"
          }
          linkKey={
            hasLiabilityInsurance
              ? "signup.user_info.terms_liability.link.url"
              : "signup.user_info.terms.link.url"
          }
          selected={customer.termsAccepted}
          onSelect={() => {
            dispatch(
              updateCustomer({ termsAccepted: !customer.termsAccepted })
            );
          }}
        />
        <Gutter size={2} />
        <AcceptBox
          textKey="signup.user_info.marketing_permission.description"
          linkKey="signup.user_info.marketing_permission.link.url"
          selected={customer.marketingPermissionAccepted}
          onSelect={() => {
            dispatch(
              updateCustomer({
                marketingPermissionAccepted:
                  !customer.marketingPermissionAccepted,
              })
            );
          }}
        />
      </ChatMessage>
    </>
  );
};

type AcceptBoxProps = {
  onSelect: () => void;
  selected: boolean;
  textKey: string;
  linkKey: string;
};

const AcceptBox = ({
  onSelect,
  selected,
  textKey,
  linkKey,
}: AcceptBoxProps) => {
  const { t } = useWebTranslation();

  return (
    <FlexRow
      justifyContent="space-between"
      className={styles.acceptBox}
      onClick={onSelect}
    >
      <div className={styles.acceptBoxCheckbox}>
        <Checkbox active={selected} />
      </div>
      <Typography variant="body4">
        <Trans
          i18nKey={textKey}
          components={{
            a: (
              <a
                className={styles.acceptBoxLink}
                href={t(linkKey)}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </Typography>
    </FlexRow>
  );
};

export default UserInfoForm;
